import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { OAuthService } from 'angular-oauth2-oidc';
import { User } from "../../models/user";
import { ConfigService } from '../config/config-service';
import { DataService } from '../data/data.service';
import { SessionService } from "../session/session.service";
import { ensureSentryInitialized } from '../../utilities/sentry';

@Injectable({
    providedIn: 'root'
})
export class AppInitializerService {

    constructor(public sessionService: SessionService,
                public http: HttpClient,
                public config: ConfigService,
                public oauth: OAuthService,
                public dataService: DataService) {
    }

    initApp(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.http.get('/config').subscribe((serverConfig: any) => {

                if (this.sessionService.getXApiKey()) {
                    this.getUser(resolve, reject);
                } else {
                    // User Keycloak login
                    const oauthConfig = Object.assign(this.config.get('oauth2'), serverConfig.oauth2);
                    this.config.set('oauth2', oauthConfig);
                    const envConfig = Object.assign(this.config.get('environment'), serverConfig.environment);
                    this.config.set('environment', envConfig);


                    this.oauth.configure(oauthConfig);
                    this.oauth.loadDiscoveryDocumentAndTryLogin().then(() => {
                        if (!this.oauth.hasValidAccessToken()) {
                            sessionStorage.setItem(this.config.get('sessionRedirectName'), window.location.pathname + window.location.search);
                            this.oauth.initLoginFlow();
                        } else {
                            this.oauth.setupAutomaticSilentRefresh();
                            this.getUser(resolve, reject);
                        }
                    }, async (err) => {
                        // MVPEC-13410: loadDiscoveryDocumentAndTryLogin() rejects and dead-ends the
                        // user on the API key page. Triggered by a direct application link (the ack
                        // deep link, embedded in SMS/email/Teams and opened from any device) when the
                        // device cannot complete the OAuth round-trip to the SSO issuer - currently
                        // localized to off-network (UK-region) recipients. Capture the failure reason,
                        // the issuer being reached, and whether PKCE/nonce/code survived so we can
                        // distinguish reachability (status 0, no code) from storage-loss or token
                        // validation. Runs during APP_INITIALIZER, before SentryErrorHandler inits
                        // Sentry, so ensure init first or captureException no-ops.
                        ensureSentryInitialized({ environment: this.config.get('environment.platform') });
                        const hasPkce = !!sessionStorage.getItem('PKCE_verifier');
                        const hasNonce = !!sessionStorage.getItem('nonce');
                        const hasCodeParam = /[?&]code=/.test(window.location.search);
                        const hasStateParam = /[?&]state=/.test(window.location.search);
                        // Keep extra small (scalars only, no raw error object): the event is sent as
                        // the page navigates away, and the transport's keepalive only survives
                        // navigation when the body stays under ~60KB. err.message/url are scrubbed by
                        // Sentry anyway, so we omit the bulky originalError.
                        Sentry.captureException(new Error('Ack deep-link OAuth tryLogin failed'), {
                            tags: { flow: 'ack-deeplink', step: 'tryLogin' },
                            extra: {
                                errType: err?.type,
                                errName: err?.name,
                                errStatus: err?.status,
                                errStatusText: err?.statusText,
                                oauthIssuer: this.config.get('oauth2.issuer'),
                                hasPkce,
                                hasNonce,
                                hasCodeParam,
                                hasStateParam,
                                url: window.location.href
                            }
                        });
                        // Wait for the event to flush before navigating so the redirect does not
                        // cancel the in-flight request. flush's own timeout is the safety floor -
                        // it resolves (false) at the deadline rather than hanging, so we always
                        // redirect. Keycloak was not working -> redirect to API login.
                        await Sentry.flush(3000);
                        window.location.href = "./assets/apiKeyLogon.html";
                    });
                }
            });
        });
    }

    getUser(resolve: (value: User) => void, reject: (reason?: any) => void) {
        this.sessionService.getUser().subscribe({
            next: (user: User) => {
                if ( user ) {
                    resolve(user);
                } else {
                    // TODO: Display a general major error has happened (that should never happen)
                    console.error();
                    reject();
                }
            },
            error: (err) => {
                if ( err.status === 401 ) {
                    sessionStorage.setItem(this.config.get('sessionRedirectName'), window.location.pathname + window.location.search);
                    this.sessionService.logout();
                    reject();
                } else {
                    // TODO: Display a general major error has happened (that should never happen)
                    console.error();
                    reject();
                }
            }
        });
    }
}
